import React, { Component } from 'react';
import _InterviewScheduleService from 'modules/InterviewScheduleService';
import _utils from 'modules/Utils';
import _notification from 'modules/notification';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PROJECT_LOGO } from '../../constants/constants';
import _branding from 'modules/branding';
// Branding/UI Artifact Forensic Audit — Final Re-Investigation phase: switched Back/Print from
// `ButtonComponent` (`btn-secondary`/`btn-tertiary`) to antd `Button` + `button-global` classes —
// the same pattern now used by ResultCard_new.jsx and StudentAppointmentLetter.jsx — so the
// Interview Call Letter and Appointment Letter (explicitly "two stages of the same enterprise
// recruitment product") share one consistent button design, not two different ones.
import { Spin, Button } from 'antd';
// Phase C.5 (Candidate System UI Language Migration): only the page chrome OUTSIDE the printable
// letter payload (#outerBorder/#divReceiptPrint below) is migrated — see modules/i18n/pages/
// interviewCallLetter.js for why the payload itself (including the admin-authored letter body) is
// deliberately left untouched.
import Text from 'components/Text/Text';

// Phase C (Interview Call Letter).
//
// InterviewSchedule is now authoritative for Date/Time/Venue/Post — this screen no longer reads the
// legacy StudentInterviewTimeData mechanism (getStudentInterviewTimeDateByApplicationID) and no longer
// merges placeholders client-side. The server (InterviewScheduleService.GetCallLetterForCandidate)
// resolves the caller's own Application Id from the session, merges Post.InterviewLetterNote against
// the caller's own Released InterviewSchedule row, and returns the fully-merged HTML only if the Call
// Letter itself has been explicitly released — never inferred from the schedule's own Released state,
// and never trusting any candidate-supplied Application Id/Roll Number/Schedule Id.
class InterviewCallLetter extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            callLetter: null,
            isDownloading: false
        };
    }

    componentDidMount() {
        this.loadCallLetter();
    }

    loadCallLetter = async () => {
        this.setState({ loading: true });
        try {
            const response = await _InterviewScheduleService.getCallLetterForCandidate();
            this.setState({ callLetter: (response.status === 200 && response.data) || null, loading: false });
        } catch (e) {
            this.setState({ loading: false, callLetter: null });
            _notification.showMessage('error', 'Something went wrong. Please try again.');
        }
    }

    onPrint = () => {
        this.setState({ isDownloading: true });
        const originalDiv = document.querySelector("#divReceiptPrint");

        // Clone the div to avoid modifying the original
        const clonedDiv = originalDiv.cloneNode(true);
        clonedDiv.style.position = "absolute";
        clonedDiv.style.left = "-9999px";
        clonedDiv.style.width = "1141.89px";
        document.body.appendChild(clonedDiv);


        html2canvas(clonedDiv, {
            scale: 3, allowTaint: true, useCORS: true
        }).then(canvas => {
            const imgData = canvas.toDataURL('image/jpeg', 0.8);

            const doc = new jsPDF({
                orientation: 'p',
                unit: 'pt',
                format: 'a4',
                compressPdf: true
            });

            const pageWidth = 595.28;
            const pageHeight = 841.89;

            const margin = 1; // Margin for all sides
            const contentWidth = pageWidth - margin * 2;
            const contentHeight = pageHeight - margin * 2;

            const widthRatio = contentWidth / canvas.width;
            const heightRatio = contentHeight / canvas.height;
            const ratio = Math.min(widthRatio, heightRatio);

            const canvasWidth = canvas.width * ratio;
            const canvasHeight = canvas.height * ratio;

            // Center the image within the margins
            const xOffset = margin + (contentWidth - canvasWidth) / 2;
            const yOffset = margin;

            doc.addImage(imgData, 'JPEG', xOffset, yOffset, canvasWidth, canvasHeight);
            // Meaningful, candidate-specific filename (never a generic "Candidate_InterviewCallLetter.pdf").
            doc.save(_utils.buildInterviewCallLetterFileName(this.state.callLetter));
            document.body.removeChild(clonedDiv);
            this.setState({ isDownloading: false });

            // Best-effort audit of the download itself — never blocks/delays the PDF the candidate already has.
            _InterviewScheduleService.recordCallLetterDownload().catch(() => { });
        });

    }


    onCancel = () => {
        this.props.onTabOrderChange(-1);
    }

    render() {
        const { loading, callLetter } = this.state;

        if (loading)
            return <div role="status" aria-live="polite"><Spin size="large" /></div>

        return (
            <>
                <div className="candidate-interview-letter ch-container gl-bxbr analysisWrap pt-3 pb-3 p-sm-4 p-md-5">
                    <h5 className="heading mb-4"><Text k="interviewCallLetter.pageHeading" /></h5>

                    {!callLetter && (
                        <div className="candidateform-cover pt-3 pr-3 pl-3 pb-3 candidate-result-wrap text-center">
                            <p className="mb-0">Your Interview Call Letter has not been released yet. Please check back later.</p>
                        </div>
                    )}

                    {callLetter && (
                        // Quick fix (formatting): `textAlign:'center'` on these two wrapper divs was
                        // cascading into every paragraph of the merged letter body that didn't have its
                        // own explicit alignment — reported as "Candidates are requested to report..."
                        // rendering centrally aligned when it should read as normal left-aligned body
                        // text. Centering now only applies to the logo's own wrapper (below), where it
                        // belongs; the letter body's alignment is governed by .interviewCall (App.scss)
                        // instead. Padding bumped 7px→45px — the old value left text visibly touching the
                        // page edge ("margins too small"), not a standard letter margin.
                        <div id="outerBorder" style={{ border: 'solid', borderColor: 'gray' }}>
                            <div id="divReceiptPrint" style={{ border: 'solid', borderColor: 'transparent', padding: 45 }}>

                                <div style={{ textAlign: 'center' }}>
                                    {/* Phase C.7.2 (Branding Foundation): resolved through the unified priority chain
                                        (Portal_Logo → branding LogoUrl → PROJECT_LOGO) instead of a hardcoded env-config logo. */}
                                    <img alt='PROJECT_LOGO' src={_branding.resolveLogoUrl(localStorage['Portal_Logo'], PROJECT_LOGO)} style={{ alignSelf: 'center', cursor: "pointer", width: "200px" }} />
                                </div>

                                <div>
                                    <div className='interviewCall' dangerouslySetInnerHTML={{ __html: callLetter.MergedHtml }}></div>
                                </div>

                            </div>
                        </div>
                    )}

                    <div className="row mt-4 footer-buttons">
                        <div className="col-12">
                            <div className="form-action d-flex justify-content-center flex-wrap">
                                <Button
                                    id="btnCancel"
                                    className="button-global button-border autoHeight ml-4 mb-2 autoWidth"
                                    onClick={this.onCancel}
                                ><div className='displayInGrid'><Text k="interviewCallLetter.backButton" /></div></Button>

                                {callLetter && (
                                    <Button
                                        id="btnPrint"
                                        className="button-global button-primary autoHeight ml-4 mb-2 autoWidth"
                                        onClick={this.onPrint}
                                    ><div className='displayInGrid'><Text k="interviewCallLetter.printDownloadButton" /></div></Button>
                                )}
                            </div>
                        </div>
                    </div>
                    {this.state.isDownloading && <div role="status" aria-live="polite"><Spin size="large" /></div>}
                </div>
            </>
        )
    };
}

export default InterviewCallLetter;
